<template>
  <div class="page-content">
    <div class="models-page-heading">
      <h1>Models</h1>
      <v-btn
        v-if="!isSatelliteMonitor && !isSupportAccount"
        class="btn-secondary"
        @click="openAddDialog"
      >
        Add Performer
      </v-btn>
    </div>

    <!-- Tabs Card with Add Performer Button -->
    <v-card class="mx-auto elevation-2 tabs-card mt-5">
      <v-container fluid>
        <v-row align="center" justify="space-between">
          <v-tabs v-model="tab" class="dashboard-tab">
            <v-tab v-for="tabItem in visibleTabItems" :key="tabItem.value" :value="tabItem.value">
              <span>{{ tabItem.label }}</span>
              <span
                v-if="activeModelDataSection === 'banking' && bankAttentionLocationCounts[tabItem.value] > 0"
                class="dashboard-tab__bank-count"
                :title="`${tabItem.label}: ${bankAttentionLocationCounts[tabItem.value]} banking attention needed`"
              >
                {{ bankAttentionLocationCounts[tabItem.value] }}
              </span>
            </v-tab>
          </v-tabs>
        </v-row>
      </v-container>
    </v-card>
  
    <div v-if="!isSatelliteMonitor" class="model-data-navigation">
      <div class="model-data-navigation__search-slot">
        <v-text-field
          v-model="searchQuery"
          class="model-data-navigation__search"
          density="compact"
          variant="outlined"
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          placeholder="Search models"
          aria-label="Search models by full name or stage name"
        ></v-text-field>
      </div>
      <v-tabs
        v-model="activeModelDataSection"
        class="model-data-navigation__tabs"
        density="compact"
        show-arrows
        @update:model-value="jumpToModelDataSection"
      >
        <v-tab
          v-for="section in modelDataSections"
          :key="section.value"
          :value="section.value"
          class="model-data-navigation__button"
        >
          <span>{{ section.label }}</span>
          <span
            v-if="section.value === 'banking' && unlinkedBankModelCount > 0"
            class="model-data-navigation__bank-count"
            :title="`${unlinkedBankModelCount} active models need bank linking or an update`"
          >
            {{ unlinkedBankModelCount }}
          </span>
        </v-tab>
      </v-tabs>
    </div>

    <div
      v-if="!isSatelliteMonitor"
      ref="modelDataTableScroll"
      class="model-data-table-wrap"
      @scroll.passive="handleModelDataHorizontalScroll"
    >
      <table class="model-data-table">
        <thead>
          <tr class="model-data-table__group-row">
            <th
              class="model-data-table__sticky model-data-table__name-group-heading"
            >{{ modelDataPrimaryShiftLabel }}</th>
            <th
              v-for="segment in modelDataGroupHeaderSegments"
              :key="segment.key"
              :colspan="segment.colspan"
              :data-model-column-group="segment.groupValue"
              class="model-data-table__group-heading"
            >
              {{ segment.label }}
            </th>
          </tr>
          <tr class="model-data-table__field-row">
            <th class="model-data-table__sticky model-data-table__name-heading">Full Name</th>
            <template v-for="group in modelDataColumnGroups" :key="`${group.value}-fields`">
              <th
                v-for="column in group.columns"
                :key="`${group.value}-${column.key}`"
                :style="modelDataColumnStyle(column)"
                :class="{ 'model-data-table__compact-column': column.compact }"
              >
                {{ column.label }}
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <template v-for="item in displayedModelRows" :key="item.id">
            <tr
              v-if="item.addSpacerBefore"
              :class="['model-data-table__group-spacer', `model-data-table__group-spacer--${String(item.location || '').toLowerCase()}`]"
            >
              <td class="model-data-table__sticky model-data-table__group-spacer-shift">
                {{ item.location }}
              </td>
              <td
                v-for="segment in modelDataGroupHeaderSegments"
                :key="`${item.id}-${segment.key}-separator`"
                :colspan="segment.colspan"
                class="model-data-table__group-spacer-section"
              >
                {{ segment.label }}
              </td>
            </tr>
            <tr
            :class="{
              'model-data-table__row--selected': selectedModelData?.id === item.id,
            }"
          >
            <td class="model-data-table__sticky model-data-table__model-name">
              <div class="model-data-table__model-cell">
                <button
                  type="button"
                  class="model-data-table__model-link"
                  title="Open model profile"
                  @click="viewModel(item)"
                >
                  {{ item.fullName }}
                </button>
                <v-btn
                  icon
                  class="model-data-table__details-button"
                  density="compact"
                  size="x-small"
                  title="Show model information below"
                  aria-label="Show model information below"
                  @click="showModelDataBelow(item)"
                >
                  <v-icon size="19">mdi-chevron-down-circle-outline</v-icon>
                </v-btn>
              </div>
            </td>
            <template v-for="group in modelDataColumnGroups" :key="`${item.id}-${group.value}`">
              <td
                v-for="column in group.columns"
                :key="`${item.id}-${group.value}-${column.key}`"
                :style="modelDataColumnStyle(column)"
                :class="[
                  { 'model-data-table__compact-column': column.compact },
                  inlineFieldStateClass(item, column),
                  rutStatusClass(item, column),
                  bankLinkStatusClass(item, column),
                ]"
              >
                <span
                  v-if="isSupportAccount"
                  :class="{ 'model-data-empty-value': !modelDataCellValue(item, column) }"
                >{{ modelDataDisplayValue(item, column) || '—' }}</span>
                <button
                  v-else-if="column.key === 'status'"
                  type="button"
                  class="model-data-table__status-button"
                  :title="inlineFieldValue(item, column) === 'Active' ? 'Set model inactive' : 'Set model active'"
                  :aria-label="inlineFieldValue(item, column) === 'Active' ? 'Set model inactive' : 'Set model active'"
                  :disabled="isInlineModelSaving(item)"
                  @click="toggleInlineStatus(item, column)"
                >
                  <v-icon
                    :color="inlineFieldValue(item, column) === 'Active' ? '#2c74b3' : 'error'"
                    size="18"
                  >
                    {{ inlineFieldValue(item, column) === 'Active' ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                </button>
                <select
                  v-else-if="inlineFieldOptions(column)"
                  :value="inlineFieldValue(item, column)"
                  class="model-data-table__inline-control model-data-table__inline-select"
                  :aria-label="`Edit ${column.label}`"
                  :disabled="isInlineModelSaving(item)"
                  @focus="beginInlineEdit(item, column)"
                  @change="updateInlineDraft(item, column, $event.target.value); saveInlineField(item, column)"
                >
                  <option v-if="inlineFieldAllowsBlank(column)" value="">—</option>
                  <option
                    v-for="option in inlineFieldOptions(column)"
                    :key="`${column.key}-${option.value}`"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <input
                  v-else-if="inlineFieldType(column)"
                  :value="inlineFieldValue(item, column)"
                  :type="inlineInputType(item, column)"
                  class="model-data-table__inline-control model-data-table__inline-input"
                  :class="{ 'model-data-table__inline-input--password': column.password }"
                  :aria-label="`Edit ${column.label}`"
                  :disabled="isInlineModelSaving(item)"
                  autocomplete="off"
                  @focus="beginInlineEdit(item, column)"
                  @input="updateInlineDraft(item, column, $event.target.value)"
                  @blur="saveInlineField(item, column)"
                  @keydown.enter.prevent="$event.target.blur()"
                />
                <span v-if="column.password" class="model-data-table__password-actions">
                  <button
                    type="button"
                    :title="isModelDataFieldRevealed(item, column) ? 'Hide password' : 'Show password'"
                    :aria-label="isModelDataFieldRevealed(item, column) ? 'Hide password' : 'Show password'"
                    @mousedown.prevent
                    @click="toggleModelDataFieldVisibility(item, column)"
                  >
                    <v-icon size="14">
                      {{ isModelDataFieldRevealed(item, column) ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}
                    </v-icon>
                  </button>
                  <button
                    type="button"
                    title="Copy password"
                    aria-label="Copy password"
                    @mousedown.prevent
                    @click="copyModelDataField(item, column)"
                  >
                    <v-icon size="13">mdi-content-copy</v-icon>
                  </button>
                </span>
                    <span
                      v-if="column.key !== 'status' && !inlineFieldOptions(column) && !inlineFieldType(column)"
                      :class="{ 'model-data-empty-value': !modelDataCellValue(item, column) }"
                    >
                      {{ modelDataCellValue(item, column) || '—' }}
                    </span>
                <v-icon
                  v-if="inlineFieldState(item, column)"
                  class="model-data-table__save-indicator"
                  :class="`model-data-table__save-indicator--${inlineFieldState(item, column)}`"
                  size="12"
                >
                  {{ inlineFieldStateIcon(item, column) }}
                </v-icon>
              </td>
            </template>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <v-data-table
      v-else
      :headers="displayHeaders"
      :items="filteredModels"
      :items-per-page="-1"
      class="elevation-1 mt-5"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.fullName }}</td>
          <td>{{ item.stageName }}</td>
          <td>{{ item.paginaAjuste || '' }}</td>
          <td>{{ studioCode(item.studio_id) }}</td>
          <td>{{ item.status }}</td>
          <td>{{ item.renta === 1 ? 'Yes' : 'No' }}</td>
          <td>{{ item.startDate }}</td>
          <td>{{ item.docNumber }}</td>
          <td class="model-data-empty-value">—</td>
          <td>
            <v-btn icon class="btn-primary" density="comfortable" style="margin-right: 4px;" @click="viewModel(item)">
              <v-icon>mdi-account-details</v-icon>
            </v-btn>
            <v-btn v-if="!isSupportAccount" icon class="btn-primary" density="comfortable" style="margin-right: 4px;" @click="editModel(item)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <div
      v-if="!isSatelliteMonitor"
      ref="modelDataPreview"
      class="model-data-preview"
    >
      <div v-if="selectedModelData" class="model-data-preview__model-heading">
        <div v-if="modelDataEditMode" class="model-data-preview__header-fields">
          <label class="model-data-preview__header-field model-data-preview__header-field--name">
            <span>Full Name</span>
            <input
              v-model="modelDataDraft.fullName"
              type="text"
              autocomplete="off"
              placeholder="Full name"
            />
          </label>
          <label class="model-data-preview__header-field model-data-preview__header-field--stage">
            <span>Stage Name</span>
            <input
              v-model="modelDataDraft.stageName"
              type="text"
              autocomplete="off"
              placeholder="Stage name"
            />
          </label>
        </div>
        <div v-else class="model-data-preview__header-summary">
          <div class="model-data-preview__header-summary-field">
            <span>Full Name</span>
            <div class="model-data-preview__model-name">{{ selectedModelData.fullName }}</div>
          </div>
          <div class="model-data-preview__header-summary-field">
            <span>Stage Name</span>
            <div class="model-data-preview__stage-name">{{ selectedModelData.stageName || 'No stage name' }}</div>
          </div>
        </div>
        <div class="model-data-preview__header-actions">
          <template v-if="modelDataEditMode">
            <v-btn
              variant="text"
              density="compact"
              class="model-data-preview__profile-button"
              :disabled="modelDataSaving"
              @click="cancelModelDataEdit"
            >Cancel</v-btn>
            <v-btn
              color="primary"
              variant="flat"
              density="compact"
              prepend-icon="mdi-content-save-outline"
              :loading="modelDataSaving"
              :disabled="!modelDataHasChanges"
              @click="saveModelDataDetails"
            >{{ modelDataCreateMode ? 'Create Performer' : 'Save Changes' }}</v-btn>
          </template>
          <v-btn
            v-else-if="!isSupportAccount"
            variant="outlined"
            density="compact"
            class="model-data-preview__profile-button"
            prepend-icon="mdi-pencil-outline"
            @click="beginModelDataEdit"
          >Edit Information</v-btn>
          <v-btn
            v-if="!modelDataCreateMode"
            variant="text"
            density="compact"
            class="model-data-preview__profile-button"
            @click="viewModel(selectedModelData)"
          >
            Open Profile
            <v-icon end size="17">mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-else class="model-data-preview__empty-state">
        <v-icon size="22">mdi-arrow-up</v-icon>
        Select the down arrow beside a model to view their information here.
      </div>
      <section
        v-for="group in modelDataColumnGroups"
        :key="group.value"
        :data-model-section="group.value"
        class="model-data-preview__section"
      >
        <div class="model-data-preview__title">
          <span>{{ group.label }}</span>
          <v-menu v-if="group.value === 'sites' && modelDataEditMode" location="bottom end">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                icon="mdi-plus"
                size="x-small"
                variant="text"
                title="Add site"
                aria-label="Add site"
                :disabled="modelDataInactiveSites.length === 0"
              ></v-btn>
            </template>
            <v-list density="compact" min-width="210">
              <v-list-item
                v-for="site in modelDataInactiveSites"
                :key="site.key"
                :title="site.label"
                @click="addModelDataSite(site)"
              ></v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div
          v-if="group.value === 'sites'"
          class="model-data-preview__sites"
          :class="`model-data-preview__sites--count-${modelDataVisibleSites.length}`"
        >
          <div
            v-for="site in modelDataVisibleSites"
            :key="site.key"
            class="model-data-preview__site-card"
          >
            <div class="model-data-preview__site-name">{{ site.label }}</div>
            <label class="model-data-preview__site-field model-data-preview__site-field--account">
              <span>Account</span>
              <input
                v-if="modelDataEditMode"
                v-model="modelDataDraft[site.accountKey]"
                type="text"
                autocomplete="off"
              />
              <strong v-else>{{ selectedModelData?.[site.accountKey] || '—' }}</strong>
              <button
                type="button"
                class="model-data-preview__site-action"
                title="Copy account"
                aria-label="Copy account"
                @click.prevent="copyModelDataValue(modelDataEditMode ? modelDataDraft[site.accountKey] : selectedModelData?.[site.accountKey], 'Account')"
              >
                <v-icon size="15">mdi-content-copy</v-icon>
              </button>
            </label>
            <label class="model-data-preview__site-field model-data-preview__site-field--password">
              <span>Password</span>
              <input
                v-if="modelDataEditMode"
                v-model="modelDataDraft[site.passwordKey]"
                :type="isModelDataFieldRevealed(selectedModelData, { key: site.passwordKey }) ? 'text' : 'password'"
                autocomplete="new-password"
              />
              <strong v-else>
                {{ selectedModelData?.[site.passwordKey]
                  ? (isModelDataFieldRevealed(selectedModelData, { key: site.passwordKey }) ? selectedModelData[site.passwordKey] : '••••••••')
                  : '—' }}
              </strong>
              <span class="model-data-preview__site-password-actions">
                <button
                  type="button"
                  title="Copy password"
                  aria-label="Copy password"
                  @click.prevent="copyModelDataValue(modelDataEditMode ? modelDataDraft[site.passwordKey] : selectedModelData?.[site.passwordKey], 'Password')"
                >
                  <v-icon size="15">mdi-content-copy</v-icon>
                </button>
                <button
                  type="button"
                  :title="isModelDataFieldRevealed(selectedModelData, { key: site.passwordKey }) ? 'Hide password' : 'Show password'"
                  :aria-label="isModelDataFieldRevealed(selectedModelData, { key: site.passwordKey }) ? 'Hide password' : 'Show password'"
                  @click.prevent="toggleModelDataFieldVisibility(selectedModelData, { key: site.passwordKey })"
                >
                  <v-icon size="15">{{ isModelDataFieldRevealed(selectedModelData, { key: site.passwordKey }) ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                </button>
              </span>
            </label>
            <label v-if="site.linkKey" class="model-data-preview__site-field">
              <span>Link</span>
              <input
                v-if="modelDataEditMode"
                v-model="modelDataDraft[site.linkKey]"
                type="text"
                autocomplete="off"
              />
              <strong v-else>{{ selectedModelData?.[site.linkKey] || '—' }}</strong>
            </label>
          </div>
          <div v-if="modelDataVisibleSites.length === 0" class="model-data-preview__sites-empty">
            No site credentials saved.
          </div>
        </div>
        <div
          v-else
          class="model-data-preview__fields"
          :class="[
            `model-data-preview__fields--${group.value}`,
            { 'model-data-preview__fields--editing': modelDataEditMode },
          ]"
        >
          <div
            v-for="column in modelDataPreviewColumns(group)"
            :key="column.key"
            class="model-data-preview__field"
            :class="[
              `model-data-preview__field--${column.key}`,
              modelDataPreviewWarningClass(column),
            ]"
          >
            <span class="model-data-preview__field-label">
              {{ column.label }}
            </span>
            <button
              v-if="modelDataEditMode && column.key === 'status'"
              type="button"
              class="model-data-preview__status-toggle"
              :class="{ 'model-data-preview__status-toggle--active': modelDataDraft.status === 'Active' }"
              :title="modelDataDraft.status === 'Active' ? 'Active — click to make inactive' : 'Inactive — click to activate'"
              :aria-label="modelDataDraft.status === 'Active' ? 'Active — click to make inactive' : 'Inactive — click to activate'"
              @click="modelDataDraft.status = modelDataDraft.status === 'Active' ? 'Inactive' : 'Active'"
            >
              <v-icon size="22">{{ modelDataDraft.status === 'Active' ? 'mdi-check-circle' : 'mdi-close-circle-outline' }}</v-icon>
            </button>
            <select
              v-else-if="modelDataEditMode && modelDataFormOptions(column)"
              v-model="modelDataDraft[column.key]"
              class="model-data-preview__input"
            >
              <option v-if="inlineFieldAllowsBlank(column)" value="">—</option>
              <option
                v-for="option in modelDataFormOptions(column)"
                :key="`${column.key}-${option.value}`"
                :value="option.value"
              >{{ option.label }}</option>
            </select>
            <input
              v-else-if="modelDataEditMode"
              v-model="modelDataDraft[column.key]"
              :type="column.editor === 'password' && !isModelDataFieldRevealed(selectedModelData, column) ? 'password' : (column.editor || 'text')"
              class="model-data-preview__input"
              autocomplete="off"
            />
            <span
              v-else
              class="model-data-preview__field-value"
              :class="{ 'model-data-empty-value': !selectedModelData || !modelDataCellValue(selectedModelData, column) }"
            >
              {{ selectedModelData ? (modelDataDisplayValue(selectedModelData, column) || '—') : '—' }}
            </span>
            <small
              v-if="modelDataEditMode && column.key === 'birthDate'"
              class="model-data-preview__date-hint"
            >MM/DD/YYYY</small>
          </div>
        </div>
      </section>
      <v-btn
        v-if="modelDataEditMode"
        class="model-data-preview__floating-save"
        color="primary"
        prepend-icon="mdi-content-save-outline"
        :loading="modelDataSaving"
        :disabled="!modelDataHasChanges"
        @click="saveModelDataDetails"
      >{{ modelDataCreateMode ? 'Create Performer' : 'Save Changes' }}</v-btn>
    </div>

    <!-- Monitors Section -->
    <div
      v-if="!isSatelliteMonitor"
      style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; height: 48px; background: linear-gradient(180deg, #1c2321 0%, #3A4542 100%); color: white; border-radius: 6px; margin-top: 28px;"
    >
      <h2 style="font-size: 18px; font-weight: 600; color: white;">Manage Monitors</h2>
      <v-btn
        class="btn-secondary"
        density="comfortable"
        @click="openAddMonitorDialog"
      >
        Add Monitor
      </v-btn>
    </div>

    <v-data-table
      v-if="!isSatelliteMonitor"
      :headers="monitorHeaders"
      :items="monitorRows"
      :items-per-page="-1"
      class="elevation-1 mt-5"
      hide-default-footer
      disable-sort
    >
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ item.fullName }}</td>
          <td>{{ item.displayName }}</td>
          <td>{{ item.shift }}</td>
          <td>{{ item.status }}</td>
          <td>{{ item.startDate }}</td>
          <td>{{ item.birthday }}</td>
          <td>
            <v-btn
              icon
              class="btn-primary"
              density="comfortable"
              style="margin-right: 4px;"
              @click="viewMonitor(item)"
            >
              <v-icon>mdi-account-details</v-icon>
            </v-btn>
            <v-btn
              icon
              class="btn-primary"
              density="comfortable"
              style="margin-right: 4px;"
              @click="editMonitor(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>

      <template v-slot:no-data>
        <div class="monitor-empty-state">
          No monitors configured yet.
        </div>
      </template>
    </v-data-table>

    <!-- Dialog for Adding/Editing Monitor Assignments -->
    <v-dialog v-model="monitorDialog" max-width="520px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditingMonitor ? 'Edit Monitor' : 'Add Monitor' }}</span>
        </v-card-title>

        <v-card-text>
          <v-form autocomplete="off">
            <v-text-field
              v-model="monitorForm.fullName"
              label="Full Name"
              outlined
            ></v-text-field>
            <v-text-field
              v-model="monitorForm.displayName"
              label="Display Name"
              outlined
            ></v-text-field>
            <v-select
              v-model="monitorForm.shiftKey"
              :items="monitorShiftOptions"
              item-title="label"
              item-value="key"
              label="Shift"
              outlined
            ></v-select>
            <v-select
              v-model="monitorForm.status"
              :items="['Active', 'Inactive']"
              label="Status"
              outlined
            ></v-select>
            <v-text-field
              v-model="monitorForm.startDate"
              label="Start Date"
              type="date"
              outlined
            ></v-text-field>
            <v-text-field
              v-model="monitorForm.birthday"
              label="Birthday"
              type="date"
              outlined
            ></v-text-field>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-btn
            v-if="isEditingMonitor"
            color="red"
            variant="text"
            @click="clearMonitor"
          >
            Clear
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" :loading="savingMonitor" @click="saveMonitor">
            Save
          </v-btn>
          <v-btn color="red" @click="monitorDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog for Adding/Editing Performer -->
    <v-dialog v-if="isSatelliteMonitor" v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isEditing ? 'Edit Performer' : 'Add New Performer' }}</span>
        </v-card-title>

        <v-card-text>
          <v-form autocomplete="off">
            <v-text-field v-model="newModel.fullName" label="Full Name" outlined></v-text-field>
            <v-text-field v-model="newModel.stageName" label="Stage Name" outlined></v-text-field>
            <v-select
              v-model="newModel.paginaAjuste"
              :items="paginaAjusteOptions"
              label="Pagina"
              outlined
              clearable
            ></v-select>
            <v-select
              v-model="newModel.location"
              :items="locationOptions"
              label="Location"
              outlined
            ></v-select>
            <v-select
              v-if="newModel.location === 'Satellite'"
              v-model="newModel.satellite_rate_type"
              :items="satelliteRateTypeOptions"
              label="Satellite Rate Type"
              outlined
            ></v-select>
            <v-select
              v-model="newModel.status"
              :items="['Active', 'Inactive']"
              label="Status"
              outlined
            ></v-select>
            <v-select
              v-model="newModel.renta"
              :items="rentaOptions"
              label="Renta"
              outlined
            ></v-select>
            <v-text-field v-model="newModel.startDate" label="Start Date" type="date" outlined></v-text-field>
            <v-row>
              <v-col cols="5">
                <v-select
                  v-model="newModel.docType"
                  :items="['Cedula', 'Passport', 'PPT', 'Other']"
                  label="Document Type"
                  outlined
                ></v-select>
              </v-col>
              <v-col cols="7">
                <v-text-field
                  v-model="newModel.docNumber"
                  label="Document Number"
                  outlined
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" @click="isEditing ? saveModel() : addModel()">{{ isEditing ? 'Save Changes' : 'Add Performer' }}</v-btn>
          <v-btn v-if="isEditing && newModel.status === 'Inactive' && models.find(m => m.id === currentModelId)?.status === 'Inactive'" icon color="red" @click="hideModel" style="position: absolute; bottom: 6px; left: 8px;">
            <v-icon>mdi-eye-off</v-icon>
          </v-btn>
          <v-btn color="red" @click="dialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import secureAxios from '../utils/secureAxios'; // Use secureAxios instead of axios
import { format, parseISO, isValid } from 'date-fns';
import {
  getStoredUser,
  isSatelliteMonitor as isSatMon,
  shouldScopeToStudio,
  modelsApiUrl,
  canCreateModels,
  defaultTabForPage,
} from '@/utils/satellliteMonitorRules';

export default {
  data() {
    return {
      tab: 'studio',
      allTabItems: [
        { value: 'studio', label: 'Studio' },
        { value: 'morning', label: 'Morning' },
        { value: 'evening', label: 'Evening' },
        { value: 'night', label: 'Night' },
        { value: 'satellite', label: 'Satellite' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ],
      dialog: false,
      isEditing: false,
      newModel: {
        fullName: '',
        stageName: '',
        paginaAjuste: '',
        location: '',
        satellite_rate_type: 'Scale',
        status: 'Active',
        renta: '',
        startDate: new Date().toISOString().split('T')[0],
        docType: 'Cedula',
        docNumber: ''
      },
      locationOptions: ['Morning', 'Evening', 'Night', 'Satellite'],
      satelliteRateTypeOptions: ['Scale', 'Fixed'],
      paginaAjusteOptions: ['Flirt4Free', 'Streamate','LiveJasmin', 'Chaturbate', 'StripChat'],
      rentaOptions: ['Yes', 'No'],
      models: [],
      headers: [
        { title: 'Full Name', key: 'fullName' },
        { title: 'Stage Name', key: 'stageName' },
        { title: 'Pagina', key: 'paginaAjuste' },
        { title: 'Location', key: 'location' },
        { title: 'Status', key: 'status' },
        { title: 'Renta', key: 'renta' },
        { title: 'Start Date', key: 'startDate' },
        { title: 'Document #', key: 'docNumber' },
        { title: 'RUT', key: 'rut' },
        { title: 'Actions', key: 'actions', sortable: false }
      ],
      monitors: [],
      monitorHeaders: [
        { title: 'Full Name', key: 'fullName' },
        { title: 'Display Name', key: 'displayName' },
        { title: 'Shift', key: 'shift' },
        { title: 'Status', key: 'status' },
        { title: 'Start Date', key: 'startDate' },
        { title: 'Birthday', key: 'birthday' },
        { title: 'Actions', key: 'actions', sortable: false }
      ],
      monitorDialog: false,
      isEditingMonitor: false,
      savingMonitor: false,
      monitorForm: {
        shiftKey: 'morning',
        fullName: '',
        displayName: '',
        status: 'Active',
        startDate: '',
        birthday: '',
      },
      monitorShiftOptions: [
        { key: 'morning', label: 'Morning' },
        { key: 'evening', label: 'Evening' },
        { key: 'night', label: 'Night' }
      ],
      currentModelId: null,
      searchQuery: '',
      activeModelDataSection: 'general',
      modelDataSectionScrollLock: null,
      modelDataSectionScrollTimer: null,
      selectedModelData: null,
      modelDataEditMode: false,
      modelDataCreateMode: false,
      modelDataPreviousSelection: null,
      modelDataDraft: {},
      modelDataSaving: false,
      modelDataAddedSites: [],
      modelSiteDefinitions: [
        { key: 'webmail', label: 'Webmail', accountKey: 'webmailAccount', passwordKey: 'webmailPassword' },
        { key: 'f4f', label: 'Flirt4Free', accountKey: 'f4fAccount', passwordKey: 'f4fPassword' },
        { key: 'streamate', label: 'Streamate', accountKey: 'streamateAccount', passwordKey: 'streamatePassword' },
        { key: 'liveJasmin', label: 'LiveJasmin', accountKey: 'liveJasminAccount', passwordKey: 'liveJasminPassword' },
        { key: 'chaturbate', label: 'Chaturbate', accountKey: 'chaturbateAccount', passwordKey: 'chaturbatePassword' },
        { key: 'stripChat', label: 'StripChat', accountKey: 'stripChatAccount', passwordKey: 'stripChatPassword' },
        { key: 'kwiky', label: 'Kwiky', accountKey: 'kwikyAccount', passwordKey: 'kwikyPassword' },
        { key: 'sinParty', label: 'SinParty', accountKey: 'sinPartyAccount', passwordKey: 'sinPartyPassword' },
        { key: 'cams', label: 'Cams', accountKey: 'camsAccount', passwordKey: 'camsPassword' },
        { key: 'cam4', label: 'Cam4', accountKey: 'cam4Account', passwordKey: 'cam4Password' },
        { key: 'bonga', label: 'Bonga', accountKey: 'bongaAccount', passwordKey: 'bongaPassword' },
        { key: 'xlove', label: 'XLove (Legacy)', accountKey: 'xloveAccount', passwordKey: 'xlovePassword', linkKey: 'xloveLink' },
      ],
      inlineFieldStates: {},
      inlineOriginalValues: {},
      inlineDraftValues: {},
      inlineSaveTimers: {},
      revealedModelDataFields: {},
      modelDataSections: [
        { value: 'general', label: 'General', fields: [] },
        {
          value: 'personal',
          label: 'Personal',
          fields: ['Date of Birth', 'Phone', 'Emergency Contact', 'Emergency Phone', 'Address', 'Neighborhood / City', 'Personal Email'],
        },
        {
          value: 'banking',
          label: 'Banking',
          fields: ['Bank', 'Account Number', 'Account Type', 'Linked', 'Renta'],
        },
        {
          value: 'sites',
          label: 'Sites',
          fields: ['Flirt4Free', 'Streamate', 'LiveJasmin', 'Chaturbate', 'StripChat', 'Kwiky', 'SinParty', 'Cams', 'Cam4', 'Bonga', 'XLove (Legacy)'],
        },
        {
          value: 'social',
          label: 'Social',
          fields: ['Twitter / X', 'Instagram', 'TikTok', 'Snapchat', 'Reddit', 'Telegram', 'Amazon Wishlist', 'Lovense'],
        },
        {
          value: 'additional',
          label: 'Additional Info',
          fields: ['Referred By', 'EPS', 'Health Regime'],
        },
      ],
    };
  },
  computed: {
    isSatelliteMonitor() {
      return isSatMon();
    },
    isSupportAccount() {
      return getStoredUser()?.role === 'support';
    },
    visibleTabItems() {
      if (this.isSatelliteMonitor) {
        return [
          { value: 'satellite', label: 'Satellite' },
          { value: 'inactive', label: 'Inactive' }
        ];
      }
      return this.allTabItems;
    },
    bankAttentionModels() {
      return this.models.filter((model) => (
        model.status === 'Active'
        && [0, '0', false, 'No', 2, '2', 'Update'].includes(model.bankLinked)
      ));
    },
    unlinkedBankModelCount() {
      return this.bankAttentionModels.length;
    },
    bankAttentionLocationCounts() {
      return {
        studio: this.bankAttentionModels.filter((model) => (
          ['Morning', 'Evening', 'Night'].includes(model.location)
        )).length,
        satellite: this.bankAttentionModels.filter((model) => model.location === 'Satellite').length,
      };
    },
    displayHeaders() {
      // For Satellite monitor, replace the Location column title with Studio
      if (!this.isSatelliteMonitor) return this.headers;
      return this.headers.map(h => {
        if (h.key === 'location') {
          return { ...h, title: 'Studio' };
        }
        return h;
      });
    },
    modelDataColumnGroups() {
      const credentialColumns = (prefix, label) => [
        { key: `${prefix}Account`, label, profile: true, editor: 'text' },
        { key: `${prefix}Password`, label: 'Password', profile: true, editor: 'password', password: true },
      ];
      return [
        {
          value: 'general',
          label: 'General',
          columns: [
            { key: 'stageName', label: 'Stage Name', width: 145, editor: 'text' },
            { key: 'paginaAjuste', label: 'Page', width: 105, editor: 'select' },
            { key: 'location', label: 'Shift', width: 82, compact: true, editor: 'select' },
            { key: 'status', label: 'Status', width: 58, compact: true, editor: 'status' },
            { key: 'startDate', label: 'Start Date', width: 102, compact: true, editor: 'date' },
            { key: 'docType', label: 'Doc Type', width: 92, compact: true, editor: 'select' },
            { key: 'docNumber', label: 'Document Number', width: 124, compact: true, editor: 'text' },
            { key: 'rut', label: 'RUT', width: 92, compact: true, profile: true, editor: 'select' },
          ],
        },
        {
          value: 'personal',
          label: 'Personal',
          columns: [
            { key: 'birthDate', label: 'Date of Birth', width: 108, compact: true, profile: true, editor: 'date' },
            { key: 'phone', label: 'Phone', width: 118, compact: true, profile: true, editor: 'text' },
            { key: 'emergencyContact', label: 'Emergency Contact', width: 150, profile: true, editor: 'text' },
            { key: 'emergencyPhone', label: 'Emergency Phone', width: 118, compact: true, profile: true, editor: 'text' },
            { key: 'address', label: 'Address', profile: true, editor: 'text' },
            { key: 'neighborhoodCity', label: 'Neighborhood / City', profile: true, editor: 'text' },
            { key: 'personalEmail', label: 'Personal Email', profile: true, editor: 'text' },
          ],
        },
        {
          value: 'banking',
          label: 'Banking',
          columns: [
            { key: 'bank', label: 'Bank', width: 140, profile: true, editor: 'text' },
            { key: 'accountNumber', label: 'Account Number', width: 128, compact: true, profile: true, editor: 'text' },
            { key: 'accountType', label: 'Account Type', width: 96, compact: true, profile: true, editor: 'text' },
            { key: 'bankLinked', label: 'Linked', width: 70, compact: true, profile: true, editor: 'select' },
            { key: 'renta', label: 'Renta', width: 58, compact: true, editor: 'select' },
          ],
        },
        {
          value: 'sites',
          label: 'Sites',
          columns: [
            ...credentialColumns('webmail', 'Webmail'),
            ...credentialColumns('f4f', 'Flirt4Free'),
            ...credentialColumns('streamate', 'Streamate'),
            ...credentialColumns('liveJasmin', 'LiveJasmin'),
            ...credentialColumns('chaturbate', 'Chaturbate'),
            ...credentialColumns('stripChat', 'StripChat'),
            ...credentialColumns('kwiky', 'Kwiky'),
            ...credentialColumns('sinParty', 'SinParty'),
            ...credentialColumns('cams', 'Cams'),
            ...credentialColumns('cam4', 'Cam4'),
            ...credentialColumns('bonga', 'Bonga'),
            ...credentialColumns('xlove', 'XLove (Legacy)'),
            { key: 'xloveLink', label: 'XLove Link', profile: true, editor: 'text' },
          ],
        },
        {
          value: 'social',
          label: 'Social',
          columns: [
            ...credentialColumns('twitter', 'Twitter / X'),
            ...credentialColumns('instagram', 'Instagram'),
            ...credentialColumns('tiktok', 'TikTok'),
            ...credentialColumns('snapchat', 'Snapchat'),
            ...credentialColumns('reddit', 'Reddit'),
            ...credentialColumns('telegram', 'Telegram'),
            { key: 'amazonWishlist', label: 'Amazon Wishlist', profile: true, editor: 'text' },
            ...credentialColumns('lovense', 'Lovense'),
          ],
        },
        {
          value: 'additional',
          label: 'Additional Info',
          columns: [
            { key: 'referredBy', label: 'Referred By', profile: true, editor: 'text' },
            { key: 'eps', label: 'EPS', profile: true, editor: 'text' },
            { key: 'healthRegime', label: 'Health Regime', profile: true, editor: 'text' },
          ],
        },
      ];
    },
    displayedModelRows() {
      return ['studio', 'active', 'morning', 'evening', 'night'].includes(this.tab)
        ? this.groupedModelsFiltered
        : this.filteredModels;
    },
    modelDataPrimaryShiftLabel() {
      return this.groupedModelsFiltered[0]?.location || 'Models';
    },
    modelDataVisibleSites() {
      const source = this.modelDataEditMode ? this.modelDataDraft : this.selectedModelData;
      if (!source) return [];
      return this.modelSiteDefinitions.filter((site) => (
        this.modelDataAddedSites.includes(site.key)
        || [site.accountKey, site.passwordKey, site.linkKey].filter(Boolean).some((key) => String(source[key] || '').trim())
      ));
    },
    modelDataInactiveSites() {
      const visible = new Set(this.modelDataVisibleSites.map((site) => site.key));
      return this.modelSiteDefinitions.filter((site) => !visible.has(site.key));
    },
    modelDataHasChanges() {
      if (!this.modelDataEditMode || !this.selectedModelData) return false;
      const columns = [
        { key: 'fullName' },
        ...this.modelDataColumnGroups.flatMap((group) => group.columns),
      ];
      return columns.some((column) => (
        String(this.modelDataDraft[column.key] ?? '') !== String(this.selectedModelData[column.key] ?? '')
      ));
    },
    modelDataGroupHeaderSegments() {
      return this.modelDataColumnGroups.flatMap((group) => {
        const repeatCounts = { sites: 4, social: 2 };
        const repeats = repeatCounts[group.value] || 1;
        if (repeats === 1) {
          return [{
            key: group.value,
            groupValue: group.value,
            label: group.label,
            colspan: group.columns.length,
          }];
        }

        const baseSpan = Math.floor(group.columns.length / repeats);
        const remainder = group.columns.length % repeats;
        return Array.from({ length: repeats }, (_, index) => ({
          key: `${group.value}-${index + 1}`,
          groupValue: group.value,
          label: group.label,
          colspan: baseSpan + (index >= repeats - remainder ? 1 : 0),
        }));
      });
    },
    filteredModels() {
      const tab = this.tab;
      let models = this.models;
      // Scope models for display
      const user = getStoredUser();
      if (!user) return [];

      // Normal users/admins only see their own studio models.
      // Satellite monitor sees ALL satellite models across studios.
      if (shouldScopeToStudio(user)) {
        if (!user.studio_id) return [];
        models = models.filter(m => m.studio_id === user.studio_id);
      }

      // Filter based on the selected tab
      if (tab === 'satellite') {
        models = models.filter(model =>
          model.location === 'Satellite' && model.status === 'Active'
        );
      } else if (tab === 'inactive') {
        models = models.filter(model =>
          model.status === 'Inactive'
        );
      } else if (['morning', 'evening', 'night'].includes(tab)) {
        // Morning, Evening, Night: Show only active models for the given location
        models = models.filter(model =>
          model.location.toLowerCase() === tab && model.status === 'Active'
        );
      } else if (['studio', 'active'].includes(tab)) {
        // Studio: Show all models from Morning, Evening, Night, Satellite
        // Active: Show only active models from these locations
        models = models.filter(model =>
          ['Morning', 'Evening', 'Night', 'Satellite'].includes(model.location) &&
          (tab !== 'active' || model.status === 'Active')
        );
      }

      // Apply search query filtering if there is one
      if (this.searchQuery) {
        models = models.filter(model =>
          model.fullName.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          model.stageName.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }

      // Satellite monitor: group by studio_id (CC, CJ, BOG, PEI) and sort by startDate within each group
      if (this.isSatelliteMonitor) {
        const studioOrder = { 1: 1, 2: 2, 3: 3, 4: 4 };

        models = [...models].sort((a, b) => {
          const aStudio = studioOrder[Number(a?.studio_id)] ?? 999;
          const bStudio = studioOrder[Number(b?.studio_id)] ?? 999;
          if (aStudio !== bStudio) return aStudio - bStudio;

          // startDate is formatted as YYYY-MM-DD in fetchModels()
          const aDate = a?.startDate || '9999-12-31';
          const bDate = b?.startDate || '9999-12-31';
          if (aDate !== bDate) return aDate.localeCompare(bDate);

          // Tiebreaker: stage name
          const aName = String(a?.stageName || a?.fullName || '').toLowerCase();
          const bName = String(b?.stageName || b?.fullName || '').toLowerCase();
          return aName.localeCompare(bName);
        });
      }
      return models;
    },

    groupedModelsFiltered() {
      // Apply search filtering to grouped models
      const filtered = this.groupedModels.filter(model =>
        String(model.fullName || '').toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        String(model.stageName || '').toLowerCase().includes(this.searchQuery.toLowerCase())
      );
      return filtered.map((model, index) => ({
        ...model,
        addSpacerBefore: index > 0 && model.location !== filtered[index - 1].location,
      }));
    },

    groupedModels() {
      // Scope models for display
      const user = getStoredUser();
      if (!user) return [];

      // Normal users/admins only see their own studio models.
      // Satellite monitor sees ALL satellite models across studios.
      let models = this.models;
      if (shouldScopeToStudio(user)) {
        if (!user.studio_id) return [];
        models = models.filter(m => m.studio_id === user.studio_id);
      }
      if (['studio', 'active'].includes(this.tab)) {
        // Keep studio models grouped by shift. Spacing is added after filtering.
        const morningModels = models.filter((model) => model.location === 'Morning' && model.status === 'Active');
        const eveningModels = models.filter((model) => model.location === 'Evening' && model.status === 'Active');
        const nightModels = models.filter((model) => model.location === 'Night' && model.status === 'Active');
        const satelliteModels = models.filter((model) => model.location === 'Satellite' && model.status === 'Active');

        const taggedModels = [
          ...morningModels,
          ...eveningModels,
          ...nightModels,
          ...(this.tab === 'active' ? satelliteModels : []),
        ];

        return taggedModels;
      }

      // Satellite and inactive tabs do not need shift separators.
      if (['satellite', 'inactive'].includes(this.tab)) {
        return this.filteredModels;
      }

      // Default case, return filtered models
      return this.filteredModels;
    },
    monitorRows() {
      return this.monitors;
    },
  },
  mounted() {
    const user = getStoredUser();
    if (!user) {
      console.error('No user found in localStorage');
      return;
    }

    if (this.isSatelliteMonitor) {
      this.tab = 'satellite';
    } else {
      const forcedTab = defaultTabForPage('models', user);
      if (forcedTab) {
        this.tab = forcedTab;
      }
    }

    this.fetchModels();
    this.fetchMonitors();
    window.addEventListener('beforeunload', this.handleModelDataBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.handleModelDataBeforeUnload);
    clearTimeout(this.modelDataSectionScrollTimer);
  },
  beforeRouteLeave(to, from, next) {
    if (this.modelDataHasChanges && !confirm('Discard the unsaved model information changes?')) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    handleModelDataBeforeUnload(event) {
      if (!this.modelDataHasChanges) return;
      event.preventDefault();
      event.returnValue = '';
    },
    inlineFieldKey(item, column) {
      return `${item.id}:${column.key}`;
    },
    inlineFieldValue(item, column) {
      const key = this.inlineFieldKey(item, column);
      return Object.prototype.hasOwnProperty.call(this.inlineDraftValues, key)
        ? this.inlineDraftValues[key]
        : item[column.key];
    },
    updateInlineDraft(item, column, value) {
      this.inlineDraftValues[this.inlineFieldKey(item, column)] = value;
    },
    inlineFieldState(item, column) {
      return this.inlineFieldStates[this.inlineFieldKey(item, column)] || '';
    },
    inlineFieldStateClass(item, column) {
      const state = this.inlineFieldState(item, column);
      return state ? `model-data-table__cell--${state}` : '';
    },
    rutStatusClass(item, column) {
      if (column.key !== 'rut') return '';
      const status = this.inlineFieldValue(item, column);
      if (status === 'Received') return 'model-data-table__cell--rut-received';
      if (status === 'Requested') return 'model-data-table__cell--rut-requested';
      return 'model-data-table__cell--rut-missing';
    },
    bankLinkStatusClass(item, column) {
      if (column.key !== 'bankLinked') return '';
      const value = this.inlineFieldValue(item, column);
      if ([0, '0', false, 'No'].includes(value)) return 'model-data-table__cell--bank-unlinked';
      if ([2, '2', 'Update'].includes(value)) return 'model-data-table__cell--bank-update';
      return '';
    },
    modelDataPreviewWarningClass(column) {
      if (column.key !== 'bankLinked') return '';
      const source = this.modelDataEditMode ? this.modelDataDraft : this.selectedModelData;
      if ([0, '0', false, 'No'].includes(source?.bankLinked)) return 'model-data-preview__field--bank-unlinked';
      if ([2, '2', 'Update'].includes(source?.bankLinked)) return 'model-data-preview__field--bank-update';
      return '';
    },
    inlineFieldStateIcon(item, column) {
      const state = this.inlineFieldState(item, column);
      if (state === 'saving') return 'mdi-loading';
      if (state === 'saved') return 'mdi-check-circle';
      if (state === 'error') return 'mdi-alert-circle';
      return '';
    },
    isInlineModelSaving(item) {
      const prefix = `${item.id}:`;
      return Object.entries(this.inlineFieldStates).some(([key, state]) => (
        key.startsWith(prefix) && state === 'saving'
      ));
    },
    inlineFieldOptions(column) {
      if (column.key === 'paginaAjuste') {
        return this.paginaAjusteOptions.map((value) => ({ label: value, value }));
      }
      if (column.key === 'location') {
        return this.locationOptions.map((value) => ({ label: value, value }));
      }
      if (column.key === 'renta') {
        return [
          { label: 'Yes', value: 1 },
          { label: 'No', value: 0 },
        ];
      }
      if (column.key === 'docType') {
        return ['Cedula', 'Passport', 'PPT', 'Other'].map((value) => ({ label: value, value }));
      }
      if (column.key === 'rut') {
        return ['Missing', 'Requested', 'Received'].map((value) => ({ label: value, value }));
      }
      if (column.key === 'bankLinked') {
        return [
          { label: 'Yes', value: 1 },
          { label: 'No', value: 0 },
          { label: 'Update', value: 2 },
        ];
      }
      if (['contractSigned', 'referralRequirementsCompleted', 'referralPaymentCompleted'].includes(column.key)) {
        return [
          { label: 'Yes', value: 1 },
          { label: 'No', value: 0 },
        ];
      }
      return null;
    },
    inlineFieldAllowsBlank(column) {
      return column.key === 'paginaAjuste'
        || ['bankLinked', 'contractSigned', 'referralRequirementsCompleted', 'referralPaymentCompleted'].includes(column.key);
    },
    inlineFieldType(column) {
      return ['text', 'date', 'number', 'password'].includes(column.editor) ? column.editor : '';
    },
    inlineInputType(item, column) {
      if (column.editor !== 'password') return column.editor;
      return this.isModelDataFieldRevealed(item, column) ? 'text' : 'password';
    },
    isModelDataFieldRevealed(item, column) {
      return Boolean(this.revealedModelDataFields[this.inlineFieldKey(item, column)]);
    },
    toggleModelDataFieldVisibility(item, column) {
      const key = this.inlineFieldKey(item, column);
      if (this.revealedModelDataFields[key]) delete this.revealedModelDataFields[key];
      else this.revealedModelDataFields[key] = true;
    },
    async copyModelDataField(item, column) {
      const value = String(this.inlineFieldValue(item, column) || '');
      await this.copyModelDataValue(value, 'Password');
    },
    async copyModelDataValue(rawValue, label = 'Value') {
      const value = String(rawValue || '').trim();
      if (!value) {
        toast.info(`There is no ${label.toLowerCase()} to copy.`);
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
        toast.success(`${label} copied.`);
      } catch (error) {
        console.error(`Unable to copy model ${label.toLowerCase()}:`, error?.message || error);
        toast.error(`Unable to copy the ${label.toLowerCase()}.`);
      }
    },
    beginInlineEdit(item, column) {
      const key = this.inlineFieldKey(item, column);
      if (!Object.prototype.hasOwnProperty.call(this.inlineOriginalValues, key)) {
        this.inlineOriginalValues[key] = item[column.key];
      }
      if (this.inlineFieldStates[key] === 'error') {
        delete this.inlineFieldStates[key];
      }
    },
    clearInlineSaveTimer(key) {
      if (!this.inlineSaveTimers[key]) return;
      clearTimeout(this.inlineSaveTimers[key]);
      delete this.inlineSaveTimers[key];
    },
    finishInlineSaveState(key, state) {
      this.clearInlineSaveTimer(key);
      this.inlineFieldStates[key] = state;
      if (state === 'saved') {
        this.inlineSaveTimers[key] = setTimeout(() => {
          delete this.inlineFieldStates[key];
          delete this.inlineSaveTimers[key];
        }, 1400);
      }
    },
    inlineChangeNeedsConfirmation(item, column, previousValue) {
      if (column.key === 'docType') {
        return confirm(`Change the document type for ${item.fullName}?`);
      }
      if (column.key === 'docNumber') {
        return confirm(`Change the document number for ${item.fullName}?`);
      }
      if (['bank', 'accountNumber', 'accountType', 'bankLinked'].includes(column.key)) {
        return confirm(`Change the banking information for ${item.fullName}?`);
      }
      return true;
    },
    inlineModelPayload(item, changedColumn, changedValue) {
      const payload = {
        fullName: String(item.fullName || '').trim(),
        stageName: String(item.stageName || '').trim(),
        paginaAjuste: item.paginaAjuste || '',
        location: item.location,
        satellite_rate_type: item.satellite_rate_type || 'Scale',
        status: item.status,
        renta: Number(item.renta) === 1 ? 1 : 0,
        startDate: item.startDate,
        docType: item.docType,
        docNumber: String(item.docNumber || '').trim(),
        hidden: item.hidden,
      };
      if (changedColumn && !changedColumn.profile) payload[changedColumn.key] = changedValue;
      return payload;
    },
    validateInlineModel(item, column, value) {
      const requiredFields = {
        stageName: 'Stage Name',
        location: 'Shift',
        startDate: 'Start Date',
        docType: 'Doc Type',
        docNumber: 'Document Number',
      };
      const label = requiredFields[column.key];
      if (label && !String(value || '').trim()) {
        toast.error(`${label} cannot be empty.`);
        return false;
      }
      return true;
    },
    async saveInlineField(item, column) {
      const key = this.inlineFieldKey(item, column);
      const hasOriginal = Object.prototype.hasOwnProperty.call(this.inlineOriginalValues, key);
      const previousValue = hasOriginal ? this.inlineOriginalValues[key] : item[column.key];
      const currentValue = this.inlineFieldValue(item, column);
      if (String(previousValue ?? '') === String(currentValue ?? '')) {
        delete this.inlineOriginalValues[key];
        delete this.inlineDraftValues[key];
        if (this.inlineFieldStates[key] === 'error') delete this.inlineFieldStates[key];
        return;
      }

      if (!this.validateInlineModel(item, column, currentValue)) {
        this.finishInlineSaveState(key, 'error');
        return;
      }

      if (!this.inlineChangeNeedsConfirmation(item, column, previousValue)) {
        delete this.inlineOriginalValues[key];
        delete this.inlineDraftValues[key];
        delete this.inlineFieldStates[key];
        return;
      }

      this.finishInlineSaveState(key, 'saving');
      try {
        const response = column.profile
          ? await secureAxios.patch(`/api/models/${item.id}/profile`, {
            field: column.key,
            value: currentValue,
          })
          : await secureAxios.put(`/api/models/${item.id}`, this.inlineModelPayload(item, column, currentValue));
        const index = this.models.findIndex((model) => Number(model.id) === Number(item.id));
        const savedModel = column.profile
          ? {
            ...(index >= 0 ? this.models[index] : item),
            [column.key]: response.data.value,
          }
          : {
            ...(index >= 0 ? this.models[index] : item),
            ...response.data,
            id: item.id,
            startDate: response.data.startDate || item.startDate,
          };
        if (index >= 0) this.models.splice(index, 1, savedModel);
        if (Number(this.selectedModelData?.id) === Number(item.id)) {
          this.selectedModelData = savedModel;
        }
        delete this.inlineOriginalValues[key];
        delete this.inlineDraftValues[key];
        this.finishInlineSaveState(key, 'saved');
        if (['bankLinked', 'status'].includes(column.key)) {
          window.dispatchEvent(new CustomEvent('model-bank-link-updated'));
        }
      } catch (error) {
        console.error('Error updating model inline:', error?.message || error);
        this.finishInlineSaveState(key, 'error');
        toast.error(error?.response?.data?.message || error?.response?.data || `Unable to save ${column.label}.`);
      }
    },
    async toggleInlineStatus(item, column) {
      if (this.isInlineModelSaving(item)) return;
      const previousValue = this.inlineFieldValue(item, column);
      const nextValue = previousValue === 'Active' ? 'Inactive' : 'Active';
      const action = nextValue === 'Inactive' ? 'make inactive' : 'reactivate';
      if (!confirm(`Are you sure you want to ${action} ${item.fullName}?`)) return;
      const key = this.inlineFieldKey(item, column);
      this.inlineOriginalValues[key] = previousValue;
      this.inlineDraftValues[key] = nextValue;
      await this.saveInlineField(item, column);
    },
    modelDataFormOptions(column) {
      if (column.key === 'status') {
        return ['Active', 'Inactive'].map((value) => ({ label: value, value }));
      }
      return this.inlineFieldOptions(column);
    },
    beginModelDataEdit() {
      if (!this.selectedModelData) return;
      this.modelDataCreateMode = false;
      this.modelDataPreviousSelection = null;
      this.modelDataDraft = Object.fromEntries(
        Object.entries(this.selectedModelData).map(([key, value]) => [key, value ?? ''])
      );
      this.modelDataAddedSites = [];
      this.modelDataEditMode = true;
    },
    beginNewModelData() {
      if (this.modelDataHasChanges && !confirm('Discard the unsaved model information changes?')) return;
      this.modelDataPreviousSelection = this.modelDataCreateMode ? this.modelDataPreviousSelection : this.selectedModelData;
      const blankModel = {
        id: null,
        fullName: '',
        stageName: '',
        paginaAjuste: '',
        location: 'Morning',
        satellite_rate_type: 'Scale',
        status: 'Active',
        renta: 0,
        startDate: new Date().toISOString().split('T')[0],
        docType: 'Cedula',
        docNumber: '',
        rut: 'Missing',
      };
      this.modelDataColumnGroups.flatMap((group) => group.columns).forEach((column) => {
        if (blankModel[column.key] === undefined) blankModel[column.key] = '';
      });
      this.selectedModelData = blankModel;
      this.modelDataDraft = { ...blankModel };
      this.modelDataAddedSites = [];
      this.modelDataCreateMode = true;
      this.modelDataEditMode = true;
      this.$nextTick(() => {
        this.$refs.modelDataPreview?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },
    cancelModelDataEdit(force = false) {
      if (!force && this.modelDataHasChanges && !confirm('Discard these unsaved changes?')) return;
      const previousSelection = this.modelDataPreviousSelection;
      this.modelDataEditMode = false;
      this.modelDataCreateMode = false;
      this.modelDataDraft = {};
      this.modelDataAddedSites = [];
      this.modelDataPreviousSelection = null;
      if (this.selectedModelData?.id === null) this.selectedModelData = previousSelection;
    },
    addModelDataSite(site) {
      if (!site || this.modelDataAddedSites.includes(site.key)) return;
      this.modelDataAddedSites.push(site.key);
      [site.accountKey, site.passwordKey, site.linkKey].filter(Boolean).forEach((key) => {
        if (this.modelDataDraft[key] === undefined || this.modelDataDraft[key] === null) {
          this.modelDataDraft[key] = '';
        }
      });
    },
    async saveModelDataDetails() {
      if (!this.selectedModelData || !this.modelDataHasChanges || this.modelDataSaving) return;
      const operationalColumns = [
        { key: 'fullName' },
        ...this.modelDataColumnGroups.flatMap((group) => group.columns).filter((column) => !column.profile),
      ];
      const profileColumns = this.modelDataColumnGroups
        .flatMap((group) => group.columns)
        .filter((column) => column.profile);
      const model = Object.fromEntries(operationalColumns.map((column) => [column.key, this.modelDataDraft[column.key]]));
      model.satellite_rate_type = this.modelDataDraft.satellite_rate_type || this.selectedModelData.satellite_rate_type || 'Scale';
      const profile = Object.fromEntries(profileColumns.map((column) => [column.key, this.modelDataDraft[column.key] ?? '']));

      this.modelDataSaving = true;
      try {
        const { data } = this.modelDataCreateMode
          ? await secureAxios.post('/api/models/details', { model, profile })
          : await secureAxios.put(`/api/models/${this.selectedModelData.id}/details`, { model, profile });
        const index = this.models.findIndex((item) => Number(item.id) === Number(this.selectedModelData.id));
        const savedModel = {
          ...(index >= 0 ? this.models[index] : this.selectedModelData),
          ...data,
          id: data.id || this.selectedModelData.id,
          startDate: data.startDate || this.modelDataDraft.startDate,
        };
        if (index >= 0) this.models.splice(index, 1, savedModel);
        else this.models.push(savedModel);
        this.selectedModelData = savedModel;
        const created = this.modelDataCreateMode;
        this.cancelModelDataEdit(true);
        window.dispatchEvent(new CustomEvent('model-bank-link-updated'));
        toast.success(created ? 'Performer created.' : 'Model information saved.');
      } catch (error) {
        console.error('Unable to save model information:', error?.message || error);
        toast.error(error?.response?.data?.message || error?.response?.data || 'Unable to save model information.');
      } finally {
        this.modelDataSaving = false;
      }
    },
    showModelDataBelow(item) {
      if (this.modelDataHasChanges && Number(this.selectedModelData?.id) !== Number(item.id)) {
        if (!confirm('Discard the unsaved model information changes?')) return;
        this.cancelModelDataEdit(true);
      }
      this.selectedModelData = item;
      this.$nextTick(() => {
        const target = this.$refs.modelDataPreview;
        if (!target) return;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        target.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'start',
        });
      });
    },
    jumpToModelDataSection(sectionValue) {
      const scroller = this.$refs.modelDataTableScroll;
      const target = scroller?.querySelector(`[data-model-column-group="${sectionValue}"]`);
      if (!scroller || !target) return;
      this.activeModelDataSection = sectionValue;
      this.modelDataSectionScrollLock = sectionValue;
      clearTimeout(this.modelDataSectionScrollTimer);
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      const targetLeft = target.getBoundingClientRect().left
        - scroller.getBoundingClientRect().left
        + scroller.scrollLeft;
      const stickyNameWidth = 230;
      scroller.scrollTo({
        left: sectionValue === 'general' ? 0 : Math.max(0, targetLeft - stickyNameWidth),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
      this.modelDataSectionScrollTimer = setTimeout(
        () => this.releaseModelDataSectionScrollLock(),
        reduceMotion ? 0 : 700
      );
    },
    releaseModelDataSectionScrollLock() {
      clearTimeout(this.modelDataSectionScrollTimer);
      this.modelDataSectionScrollTimer = null;
      this.modelDataSectionScrollLock = null;
      this.handleModelDataHorizontalScroll();
    },
    handleModelDataHorizontalScroll() {
      const scroller = this.$refs.modelDataTableScroll;
      if (!scroller) return;
      if (this.modelDataSectionScrollLock) {
        this.activeModelDataSection = this.modelDataSectionScrollLock;
        clearTimeout(this.modelDataSectionScrollTimer);
        this.modelDataSectionScrollTimer = setTimeout(() => this.releaseModelDataSectionScrollLock(), 140);
        return;
      }
      const threshold = scroller.getBoundingClientRect().left + 250;
      const headings = [...scroller.querySelectorAll('[data-model-column-group]')];
      const current = headings.reduce((selected, heading) => (
        heading.getBoundingClientRect().left <= threshold ? heading : selected
      ), headings[0]);
      const sectionValue = current?.dataset?.modelColumnGroup;
      if (sectionValue) this.activeModelDataSection = sectionValue;
    },
    modelDataCellValue(item, column) {
      if (column.key === 'renta') return Number(item.renta) === 1 ? 'Yes' : 'No';
      if (column.key === 'bankLinked') {
        if (item?.bankLinked === null || item?.bankLinked === undefined || item?.bankLinked === '') return '';
        if (Number(item.bankLinked) === 2) return 'Update';
        return Number(item.bankLinked) === 1 ? 'Yes' : 'No';
      }
      if (['contractSigned', 'referralRequirementsCompleted', 'referralPaymentCompleted'].includes(column.key)) {
        if (item?.[column.key] === null || item?.[column.key] === undefined || item?.[column.key] === '') return '';
        return Number(item[column.key]) === 1 ? 'Yes' : 'No';
      }
      const value = item?.[column.key];
      return value === null || value === undefined ? '' : String(value).trim();
    },
    modelDataPreviewColumns(group) {
      if (group.value !== 'general') return group.columns;
      return group.columns.filter((column) => column.key !== 'stageName');
    },
    modelDataDisplayValue(item, column) {
      const value = this.modelDataCellValue(item, column);
      if (!value || !column.password) return value;
      return '••••••••';
    },
    modelDataColumnStyle(column) {
      if (!column?.width) return null;
      const width = `${column.width}px`;
      return { minWidth: width, width, maxWidth: width };
    },
    async fetchModels() {
      try {
        const response = await secureAxios.get(modelsApiUrl());
        this.models = response.data.map(model => ({
          ...model,
          startDate: model.startDate && isValid(parseISO(model.startDate)) ? format(parseISO(model.startDate), 'yyyy-MM-dd') : ''
        }));
      } catch (error) {
        console.error('Error fetching models:', error?.message || error);
        this.models = [];
      }
    },
    async fetchMonitors() {
      if (this.isSatelliteMonitor) {
        this.monitors = [];
        return;
      }

      const user = getStoredUser();
      if (!user?.studio_id) {
        this.monitors = [];
        return;
      }

      try {
        const { data } = await secureAxios.get(`/api/monitors?studio_id=${user.studio_id}`);
        this.monitors = this.buildMonitorRowsFromResponse(data);
      } catch (error) {
        console.error('Error fetching monitors:', error?.message || error);
        this.monitors = [];
      }
    },
    openAddDialog() {
      if (!canCreateModels()) {
        toast.error('Satellite monitors cannot create models.');
        return;
      }
      this.beginNewModelData();
    },
    async addModel() {
      if (!canCreateModels()) {
        toast.error('Satellite monitors cannot create models.');
        return;
      }
      if (!this.newModel.fullName || !this.newModel.stageName || !this.newModel.location || !this.newModel.status) {
        alert("Please fill in all required fields.");
        return;
      }

      const rentaValue = this.newModel.renta === 'Yes' ? 1 : 0;
      const newModelData = {
        ...this.newModel,
        renta: rentaValue,
        startDate: isValid(parseISO(this.newModel.startDate)) ? parseISO(this.newModel.startDate).toISOString().split('T')[0] : null
      };

      try {
        const user = getStoredUser();
        const response = await secureAxios.post('/api/models', {
          ...newModelData,
          studio_id: user?.studio_id
        });
        this.models.push(response.data);
        this.dialog = false;
        toast.success(`Model ${response.data.stageName} added successfully!`);
        this.resetForm();
      } catch (error) {
        console.error('Error adding model:', error?.message || error);

        if (error.response && error.response.status === 409 && error.response.data?.model) {
          const existing = error.response.data.model;

          alert(
            `This model already exists.\n\n` +
            `${existing.stageName || existing.fullName}\n` +
            `Studio: ${this.studioCode(existing.studio_id)}\n` +
            `Location: ${existing.location}\n` +
            `Status: ${existing.status}`
          );

          return;
        }

      }
    },
    resetForm() {
      this.newModel = {
        fullName: '',
        stageName: '',
        paginaAjuste: '',
        location: '',
        satellite_rate_type: 'Scale',
        status: 'Active',
        renta: '',
        startDate: new Date().toISOString().split('T')[0],
        docType: 'Cedula',
        docNumber: ''
      };
    },
    editModel(model) {
      try {
        const startDateFormatted = model.startDate ? format(parseISO(model.startDate), "yyyy-MM-dd") : '';

        this.dialog = true;
        this.isEditing = true;
        this.newModel = {
          ...model,
          paginaAjuste: model.paginaAjuste || '',
          satellite_rate_type: model.satellite_rate_type || 'Scale',
          startDate: startDateFormatted,
          renta: model.renta === 1 ? 'Yes' : 'No', // Convert for the select field
          docType: model.docType || 'Cedula',
          docNumber: model.docNumber || ''
        };
        this.currentModelId = model.id;
      } catch (error) {
        console.error("Error formatting date in editModel:", error?.message || error);
      }
    },
    viewModel(model) {
      const profileUrl = `${window.location.origin}/model/${model.id}/overview`;
      window.open(profileUrl, '_blank');
    },
    viewMonitor() {
      toast.info('Monitor profiles are coming next.');
    },
    editMonitor(monitor) {
      this.isEditingMonitor = true;
      this.monitorForm = {
        shiftKey: monitor.shiftKey,
        fullName: monitor.fullName || '',
        displayName: monitor.displayName || monitor.fullName || '',
        status: monitor.status || 'Active',
        startDate: monitor.startDate || '',
        birthday: monitor.birthday || '',
      };
      this.monitorDialog = true;
    },
    openAddMonitorDialog() {
      const firstEmptyShift = this.monitors.find((monitor) => !monitor.fullName && !monitor.displayName);
      this.isEditingMonitor = false;
      this.monitorForm = {
        shiftKey: firstEmptyShift?.shiftKey || 'morning',
        fullName: '',
        displayName: '',
        status: 'Active',
        startDate: '',
        birthday: '',
      };
      this.monitorDialog = true;
    },
    async saveMonitor() {
      const user = getStoredUser();
      if (!user?.studio_id) {
        toast.error('Missing studio information.');
        return;
      }

      const fullName = String(this.monitorForm.fullName || '').trim();
      const displayName = String(this.monitorForm.displayName || fullName).trim();
      if (!fullName && !displayName) {
        toast.error('Please enter a monitor name.');
        return;
      }

      const shift = this.monitorShiftOptions.find((option) => option.key === this.monitorForm.shiftKey);
      if (!shift) {
        toast.error('Please select a valid shift.');
        return;
      }

      const nextMonitors = this.monitorShiftOptions.map((option) => {
        const existing = this.monitors.find((monitor) => monitor.shiftKey === option.key) || {};
        if (option.key !== shift.key) {
          return {
            ...existing,
            id: null,
            shiftKey: option.key,
            fullName: existing.fullName || '',
            displayName: existing.displayName || existing.fullName || '',
            shift: option.label,
            status: existing.status || (existing.fullName || existing.displayName ? 'Active' : ''),
            startDate: existing.startDate || '',
            birthday: existing.birthday || '',
          };
        }

        return {
          ...existing,
          id: null,
          shiftKey: option.key,
          fullName,
          displayName,
          shift: option.label,
          status: this.monitorForm.status || 'Active',
          startDate: this.monitorForm.startDate || '',
          birthday: this.monitorForm.birthday || '',
        };
      });

      await this.saveMonitorAssignments(nextMonitors, 'Monitor saved.');
    },
    async clearMonitor() {
      const shiftKey = this.monitorForm.shiftKey;
      const nextMonitors = this.monitorShiftOptions.map((option) => {
        const existing = this.monitors.find((monitor) => monitor.shiftKey === option.key) || {};
        if (option.key !== shiftKey) {
          return {
            ...existing,
            id: null,
            shiftKey: option.key,
            fullName: existing.fullName || '',
            displayName: existing.displayName || existing.fullName || '',
            shift: option.label,
            status: existing.status || (existing.fullName || existing.displayName ? 'Active' : ''),
            startDate: existing.startDate || '',
            birthday: existing.birthday || '',
          };
        }

        return {
          ...existing,
          id: null,
          shiftKey: option.key,
          fullName: '',
          displayName: '',
          shift: option.label,
          status: '',
          startDate: '',
          birthday: '',
        };
      });

      await this.saveMonitorAssignments(nextMonitors, 'Monitor cleared.');
    },
    async saveMonitorAssignments(nextMonitors, successMessage) {
      const user = getStoredUser();
      if (!user?.studio_id) {
        toast.error('Missing studio information.');
        return;
      }

      const byShift = Object.fromEntries(nextMonitors.map((monitor) => [monitor.shiftKey, monitor]));
      const payload = {
        studio_id: user.studio_id,
        morning: byShift.morning?.fullName || byShift.morning?.displayName || '',
        evening: byShift.evening?.fullName || byShift.evening?.displayName || '',
        night: byShift.night?.fullName || byShift.night?.displayName || '',
        monitors: nextMonitors,
      };

      this.savingMonitor = true;
      try {
        const { data } = await secureAxios.post('/api/monitors', payload);
        this.monitors = this.buildMonitorRowsFromResponse(data);
        this.monitorDialog = false;
        toast.success(successMessage);
      } catch (error) {
        console.error('Error saving monitor:', error?.message || error);
        toast.error('Error saving monitor.');
      } finally {
        this.savingMonitor = false;
      }
    },
    buildMonitorRowsFromResponse(data) {
      const monitorRows = Array.isArray(data?.monitors) ? data.monitors : [];

      return this.monitorShiftOptions.map((shift) => {
        const savedMonitor = monitorRows.find((monitor) => {
          const savedShift = String(monitor?.shift || '').toLowerCase();
          return savedShift === shift.key || savedShift === shift.label.toLowerCase();
        });
        const name = savedMonitor?.fullName || data?.[shift.key] || '';
        const displayName = savedMonitor?.displayName || name;

        return {
          id: savedMonitor?.id || null,
          shiftKey: shift.key,
          fullName: name,
          displayName,
          shift: shift.label,
          status: savedMonitor?.status || (name ? 'Active' : ''),
          startDate: this.formatOptionalDate(savedMonitor?.startDate),
          birthday: this.formatOptionalDate(savedMonitor?.birthday),
        };
      });
    },
    async saveModel() {
      if (!this.isEditing) {
        console.warn('Unexpected behavior: saveModel called when not in editing mode.');
        return;
      }

      const updatedModelData = {
        ...this.newModel,
        renta: this.newModel.renta === 'Yes' ? 1 : 0
      };

      try {
        const response = await secureAxios.put(`/api/models/${this.currentModelId}`, updatedModelData);
        const index = this.models.findIndex(model => model.id === this.currentModelId);
        if (index !== -1) {
          this.models.splice(index, 1, response.data);
        }
        this.dialog = false;
        toast.success(`Changes saved for ${response.data.stageName}`);
        this.currentModelId = null;
        this.isEditing = false;
      } catch (error) {
        console.error('Error updating model:', error?.message || error);
      }
    },
    
    async hideModel() {
      if (!this.currentModelId) return;
      if (!confirm('Are you sure you want to hide this model from the frontend?')) return;

      try {
        await secureAxios.patch(`/api/models/${this.currentModelId}/hidden`, { hidden: 1 });
        this.models = this.models.filter(m => m.id !== this.currentModelId);
        this.dialog = false;
        this.currentModelId = null;
        this.isEditing = false;
      } catch (error) {
        console.error('Error hiding model:', error?.message || error);
      }
    },
    studioCode(studioId) {
      const map = {
        1: 'CC',
        2: 'CJ',
        3: 'BOG',
        4: 'PEI'
      };
      return map[Number(studioId)] || '';
    },
    formatOptionalDate(dateValue) {
      if (!dateValue) return '';
      const rawDate = String(dateValue);
      const dateOnlyMatch = rawDate.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateOnlyMatch) return dateOnlyMatch[1];
      const parsed = parseISO(rawDate);
      return isValid(parsed) ? format(parsed, 'yyyy-MM-dd') : '';
    }
  }
};
</script>

<style scoped>
.dashboard-tab .v-tab {
  background-color: white;
  color: #2C74B3;
}

.models-page-heading {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 22px 2px 0;
}

.models-page-heading h1 {
  color: #203247;
  font-size: 28px;
  letter-spacing: -0.6px;
  line-height: 1;
  margin: 0;
}

.custom-border-radius {
  border-radius: 20px;
}

.custom-search-bar {
  max-width: 170px;
}

.model-data-table__group-spacer td {
  background: #5f6f7e !important;
  border: 0 !important;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  height: 34px;
  letter-spacing: 0.35px;
  padding: 0 10px;
  text-transform: uppercase;
}

.model-data-table__group-spacer .model-data-table__group-spacer-shift {
  box-shadow: 3px 0 5px rgba(31, 53, 71, 0.12);
  text-align: left;
  z-index: 5;
}

.model-data-table__group-spacer-section {
  text-align: center !important;
}

.model-data-table__group-spacer--evening td {
  background: #516c8a !important;
}

.model-data-table__group-spacer--night td {
  background: #2c4e77 !important;
}

.model-data-navigation {
  align-items: center;
  background: #edf3f7;
  border: 1px solid #dbe3ea;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 2px 6px rgba(28, 45, 61, 0.08);
  display: flex;
  gap: 0;
  margin-top: 28px;
  overflow: hidden;
  padding: 3px 6px 3px 0;
}

.model-data-navigation__tabs {
  flex: 1 1 auto;
  margin-left: 10px;
  min-width: 0;
}

.model-data-navigation__button {
  color: #52677a;
  font-size: 0.82rem;
  font-weight: 650;
  letter-spacing: 0;
  min-width: auto;
  min-height: 32px;
  padding-inline: 13px;
  text-transform: none;
}

.model-data-navigation__button.v-tab--selected {
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(35, 66, 91, 0.14);
  color: #2c74b3;
}

.model-data-navigation__bank-count,
.dashboard-tab__bank-count {
  align-items: center;
  background: #d58b1f;
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  font-size: 0.7rem;
  font-weight: 800;
  gap: 2px;
  height: 18px;
  justify-content: center;
  line-height: 1;
  margin-left: 6px;
  min-width: 22px;
  padding: 0 5px;
}

.model-data-navigation__tabs :deep(.v-tab__slider) {
  display: none;
}

.model-data-navigation__tabs :deep(.v-slide-group__prev),
.model-data-navigation__tabs :deep(.v-slide-group__next) {
  min-width: 32px;
}

.model-data-navigation__search {
  width: 100%;
}

.model-data-navigation__search :deep(.v-field) {
  background: #ffffff;
}

.model-data-navigation__search-slot {
  border-right: 1px solid #d5e0e8;
  box-sizing: border-box;
  flex: 0 0 235px;
  padding: 2px 9px;
  width: 235px;
}

.model-data-table-wrap {
  border: 1px solid #dbe3ea;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 2px 6px rgba(28, 45, 61, 0.08);
  margin-top: 0;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  scrollbar-color: #7c9ab3 #edf2f5;
  scrollbar-width: auto;
}

.model-data-table-wrap::-webkit-scrollbar {
  height: 13px;
  width: 11px;
}

.model-data-table-wrap::-webkit-scrollbar-track {
  background: #edf2f5;
}

.model-data-table-wrap::-webkit-scrollbar-thumb {
  background: #7c9ab3;
  border: 3px solid #edf2f5;
  border-radius: 8px;
}

.model-data-table {
  border-collapse: separate;
  border-spacing: 0;
  color: #273d50;
  font-size: 0.78rem;
  min-width: 100%;
  width: max-content;
}

.model-data-table th,
.model-data-table td {
  border-bottom: 1px solid #dfe7ed;
  border-right: 1px solid #e5ebf0;
  min-width: 145px;
  padding: 9px 11px;
  text-align: left;
  white-space: nowrap;
}

.model-data-table td {
  position: relative;
}

.model-data-table th.model-data-table__compact-column,
.model-data-table td.model-data-table__compact-column {
  padding-left: 7px;
  padding-right: 7px;
}

.model-data-table th.model-data-table__compact-column {
  text-align: center;
}

.model-data-table__group-row th {
  background: #4177a8;
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 700;
  height: 34px;
  position: sticky;
  text-align: center;
  text-transform: uppercase;
  top: 0;
  z-index: 4;
}

.model-data-table__field-row th {
  background: #edf3f7;
  color: #344f65;
  font-size: 0.69rem;
  font-weight: 700;
  height: 35px;
  position: sticky;
  text-align: center;
  text-transform: uppercase;
  top: 34px;
  z-index: 3;
}

.model-data-table tbody tr:nth-child(odd) td {
  background: #ffffff;
}

.model-data-table tbody tr:nth-child(even) td {
  background: #f7f9fb;
}

.model-data-table__sticky {
  left: 0;
  min-width: 230px !important;
  position: sticky;
  width: 230px;
}

.model-data-table td.model-data-table__sticky {
  left: 0;
  position: sticky;
}

.model-data-table__name-group-heading {
  background: #4177a8 !important;
  padding-left: 10px !important;
  text-align: left !important;
  text-transform: uppercase;
  z-index: 7 !important;
}

.model-data-table__name-heading {
  background: #edf3f7 !important;
  color: #344f65 !important;
  top: 34px !important;
  z-index: 6 !important;
}

.model-data-table__model-name {
  box-shadow: 3px 0 5px rgba(31, 53, 71, 0.08);
  font-weight: 700;
  z-index: 2;
}

.model-data-table__model-cell {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.model-data-table__model-link {
  color: #1f5f96;
  cursor: pointer;
  font: inherit;
  overflow: hidden;
  text-align: left;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.model-data-table__model-link:hover {
  text-decoration: underline;
}

.model-data-table__details-button {
  background: #eaf3fb;
  color: #2c74b3;
  flex: 0 0 auto;
}

.model-data-table__row--selected td {
  background: #eef7fd !important;
}

.model-data-table__row--selected .model-data-table__model-name {
  box-shadow: inset 3px 0 0 #2c74b3, 3px 0 5px rgba(31, 53, 71, 0.08);
}

.model-data-table__inline-control {
  background: transparent;
  border: 0;
  border-radius: 3px;
  color: inherit;
  font: inherit;
  height: 26px;
  line-height: 26px;
  margin: -4px -5px;
  min-width: 0;
  outline: none;
  padding: 0 5px;
  width: calc(100% + 10px);
}

.model-data-table__inline-control:hover {
  background: rgba(44, 116, 179, 0.06);
}

.model-data-table__inline-control:focus {
  background: #ffffff;
  box-shadow: inset 0 0 0 1px #73a8d2;
}

.model-data-table__inline-control:disabled {
  cursor: wait;
  opacity: 0.72;
}

.model-data-table__inline-select {
  cursor: pointer;
  padding-right: 18px;
}

.model-data-table__inline-input--password {
  padding-right: 47px;
}

.model-data-table__password-actions {
  align-items: center;
  display: flex;
  gap: 2px;
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
}

.model-data-table__password-actions button {
  align-items: center;
  background: transparent;
  border: 0;
  color: #557086;
  cursor: pointer;
  display: inline-flex;
  padding: 2px;
}

.model-data-table__status-button {
  align-items: center;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  justify-content: flex-start;
  padding: 0;
}

.model-data-table__status-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.model-data-table__cell--rut-missing {
  background: #fff1f1 !important;
}

.model-data-table__cell--bank-unlinked {
  background: #ffe8e8 !important;
  box-shadow: inset 0 0 0 1px rgba(205, 58, 58, 0.24);
  color: #a72626;
  font-weight: 700;
}

.model-data-table__cell--bank-update {
  background: #fff1d6 !important;
  box-shadow: inset 0 0 0 1px rgba(214, 130, 26, 0.3);
  color: #a55a08;
  font-weight: 700;
}

.model-data-table__cell--saving {
  background: #f3f8fc !important;
}

.model-data-table__cell--saved {
  background: #eef9f1 !important;
}

.model-data-table__cell--error {
  background: #fff0f0 !important;
  box-shadow: inset 0 0 0 1px #dc6868;
}

.model-data-table__save-indicator {
  position: absolute;
  right: 3px;
  top: 3px;
}

.model-data-table__save-indicator--saving {
  animation: model-data-spin 0.8s linear infinite;
  color: #5d87aa;
}

.model-data-table__save-indicator--saved {
  color: #2f9b55;
}

.model-data-table__save-indicator--error {
  color: #cf3d3d;
}

@keyframes model-data-spin {
  to { transform: rotate(360deg); }
}

.model-data-section-anchor,
.model-data-preview__section {
  scroll-margin-top: 18px;
}

.model-data-empty-value {
  color: #a2afba;
  text-align: center;
}

.model-data-preview {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 22px;
  scroll-margin-top: 76px;
}

.model-data-preview__model-heading,
.model-data-preview__empty-state {
  align-items: center;
  background: #263f55;
  border-radius: 6px;
  color: #ffffff;
  display: flex;
  justify-content: space-between;
  min-height: 58px;
  padding: 10px 14px;
}

.model-data-preview__empty-state {
  gap: 9px;
  justify-content: flex-start;
}

.model-data-preview__model-name {
  font-size: 0.98rem;
  font-weight: 750;
}

.model-data-preview__stage-name {
  color: #ffffff;
  font-size: 0.86rem;
  font-weight: 650;
}

.model-data-preview__header-summary {
  align-items: flex-end;
  display: flex;
  gap: 32px;
}

.model-data-preview__header-summary-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.model-data-preview__header-summary-field > span {
  color: #bdd1df;
  font-size: 0.59rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.model-data-preview__header-fields {
  display: flex;
  flex: 1 1 auto;
  gap: 10px;
  margin-right: 18px;
  max-width: 620px;
  min-width: 0;
}

.model-data-preview__header-field {
  display: flex;
  flex: 1 1 220px;
  flex-direction: column;
  gap: 3px;
  min-width: 135px;
}

.model-data-preview__header-field--name {
  flex-grow: 1.45;
}

.model-data-preview__header-field span {
  color: #d7e5ef;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.model-data-preview__header-field input {
  background: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 4px;
  color: #243d52;
  font-size: 0.82rem;
  height: 31px;
  min-width: 0;
  outline: none;
  padding: 4px 8px;
  width: 100%;
}

.model-data-preview__header-field input:focus {
  border-color: #77b7e1;
  box-shadow: 0 0 0 2px rgba(119, 183, 225, 0.2);
}

.model-data-preview__profile-button {
  color: #ffffff;
}

.model-data-preview__header-actions {
  align-items: center;
  display: flex;
  gap: 7px;
}

.model-data-preview__section {
  background: #ffffff;
  border: 1px solid #dbe3ea;
  border-radius: 6px;
  box-shadow: 0 2px 5px rgba(28, 45, 61, 0.07);
  overflow: hidden;
}

.model-data-preview__title {
  align-items: center;
  background: #edf3f7;
  border-bottom: 1px solid #dbe3ea;
  color: #20384e;
  display: flex;
  font-size: 0.88rem;
  font-weight: 700;
  justify-content: space-between;
  padding: 9px 14px;
}

.model-data-preview__fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(145px, 1fr));
}

.model-data-preview__fields--general {
  display: flex;
  flex-wrap: wrap;
}

.model-data-preview__fields--general .model-data-preview__field {
  box-sizing: border-box;
  flex: 1 1 92px;
}

.model-data-preview__fields--general .model-data-preview__field--paginaAjuste,
.model-data-preview__fields--general .model-data-preview__field--location {
  flex-basis: 105px;
}

.model-data-preview__fields--general .model-data-preview__field--status {
  flex: 0 0 78px;
}

.model-data-preview__fields--general .model-data-preview__field--startDate {
  flex-basis: 116px;
}

.model-data-preview__fields--general .model-data-preview__field--docType {
  flex-basis: 105px;
}

.model-data-preview__fields--general .model-data-preview__field--docNumber {
  flex-basis: 130px;
}

.model-data-preview__fields--general .model-data-preview__field--rut {
  flex-basis: 88px;
}

.model-data-preview__fields--personal {
  grid-template-columns: repeat(12, minmax(0, 1fr));
}

.model-data-preview__fields--personal .model-data-preview__field--birthDate,
.model-data-preview__fields--personal .model-data-preview__field--phone,
.model-data-preview__fields--personal .model-data-preview__field--emergencyContact,
.model-data-preview__fields--personal .model-data-preview__field--emergencyPhone {
  grid-column: span 3;
}

.model-data-preview__fields--personal .model-data-preview__field--address,
.model-data-preview__fields--personal .model-data-preview__field--neighborhoodCity,
.model-data-preview__fields--personal .model-data-preview__field--personalEmail {
  grid-column: span 4;
}

.model-data-preview__field {
  border-right: 1px solid #e5ebf0;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-height: 42px;
  padding: 10px 12px;
  text-align: left;
}

.model-data-preview__field--bank-unlinked {
  background: #ffe8e8;
  box-shadow: inset 0 0 0 1px rgba(205, 58, 58, 0.24);
}

.model-data-preview__field--bank-update {
  background: #fff1d6;
  box-shadow: inset 0 0 0 1px rgba(214, 130, 26, 0.3);
}

.model-data-preview__field-label {
  align-items: center;
  color: #52677a;
  display: inline-flex;
  font-size: 0.68rem;
  font-weight: 700;
  gap: 3px;
  text-transform: uppercase;
}

.model-data-preview__field-value {
  color: #243d52;
  font-size: 0.87rem;
  font-weight: 600;
}

.model-data-preview__date-hint {
  color: #758696;
  font-size: 0.62rem;
  line-height: 1;
  margin-top: -1px;
}

.model-data-preview__input,
.model-data-preview__site-field input {
  background: #ffffff;
  border: 1px solid #b9c9d7;
  border-radius: 4px;
  color: #243d52;
  font: inherit;
  height: 31px;
  min-width: 0;
  outline: none;
  padding: 4px 7px;
  width: 100%;
}

.model-data-preview__input:focus,
.model-data-preview__site-field input:focus {
  border-color: #4c8fbd;
  box-shadow: 0 0 0 2px rgba(76, 143, 189, 0.13);
}

.model-data-preview__status-toggle {
  align-items: center;
  align-self: flex-start;
  background: transparent;
  border: 0;
  color: #a9b5bf;
  cursor: pointer;
  display: inline-flex;
  height: 31px;
  justify-content: center;
  padding: 0;
}

.model-data-preview__status-toggle--active {
  color: #2c74b3;
}

.model-data-preview__sites {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 11px;
}

.model-data-preview__sites--count-1 {
  grid-template-columns: minmax(0, 1fr);
}

.model-data-preview__sites--count-2,
.model-data-preview__sites--count-4 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.model-data-preview__site-card {
  border: 1px solid #dce5ec;
  border-radius: 5px;
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 10px;
}

.model-data-preview__site-name {
  color: #255d8b;
  font-size: 0.78rem;
  font-weight: 800;
  grid-column: 1 / -1;
  text-transform: uppercase;
}

.model-data-preview__site-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  position: relative;
}

.model-data-preview__site-field > span {
  color: #607488;
  font-size: 0.65rem;
  font-weight: 750;
  text-transform: uppercase;
}

.model-data-preview__site-field strong {
  color: #243d52;
  font-size: 0.8rem;
  min-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.model-data-preview__site-field--account input,
.model-data-preview__site-field--account strong {
  padding-right: 28px;
}

.model-data-preview__site-field--password input,
.model-data-preview__site-field--password strong {
  padding-right: 54px;
}

.model-data-preview__site-action,
.model-data-preview__site-password-actions {
  align-items: center;
  bottom: 4px;
  color: #557086;
  display: inline-flex;
  position: absolute;
  right: 4px;
}

.model-data-preview__site-action,
.model-data-preview__site-password-actions button {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  padding: 3px;
}

.model-data-preview__sites-empty {
  color: #7b8996;
  font-size: 0.82rem;
  grid-column: 1 / -1;
  padding: 12px;
  text-align: center;
}

.model-data-preview__floating-save {
  bottom: 22px;
  box-shadow: 0 5px 18px rgba(26, 57, 82, 0.28);
  position: fixed;
  right: 24px;
  z-index: 20;
}

.monitor-empty-state {
  color: #667085;
  font-size: 0.92rem;
  padding: 18px;
  text-align: center;
}
</style>
